import { AmenityKeys } from '@molecules/Amenity/icons.types';
import Image from './Image';
import MultilangText from './MultilangText';
import AccommodationSourceSettings from './AccommodationSourceSettings';
import { CARD_TYPES } from '@business/utils/creditcard';
import type { Currencies, Languages } from '@data/types/common';

export default class Property {
  id!: number;
  code!: string;
  name!: string;
  picture!: Image;
  coverImage!: Image;
  description!: MultilangText;
  address!: {
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    lat: string;
    lng: string;
  };
  amenities!: {
    lang?: MultilangText;
    name: string;
    slug: AmenityKeys;
  }[];
  additionalAmenities!: {
    name: string;
  }[];
  images!: Image[];
  bankTransferInstructions!: MultilangText;
  cancellationPolicy!: MultilangText;
  termsAndConditions!: MultilangText;
  sameDayReservation!: boolean;
  contact!: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };

  defaultCurrency!: Currencies;
  defaultLanguage!: Languages;

  custom_fields!: Dict;

  paymentMethods!: {
    creditCardsEnabled: boolean;
    bankTransfersEnabled: boolean;
    paypalEnabled: boolean;
    supportedCardTypes: CARD_TYPES[];
  };

  settings!: {
    showMap: boolean;
    askPostalAddress: boolean;
    askNationality: boolean;
    askGender: boolean;
    askGuestTaxID: boolean;
    askCompanyName: boolean;
    askCompanyTaxID: boolean;
    requirePhoneNumber: boolean;
    askEstimatedArrivalTime: boolean;
    requireAcceptTerms: boolean;
    redirectAfterReservation: boolean;
    redirectUrl: string;
    redirectDelay: number;
    requireCvv: boolean;
  };

  roomsSourceSettings!: AccommodationSourceSettings[];

  policies!: {
    checkin: string;
    checkout: string;
    lateCheckout: boolean;
    lateCheckoutHour: string;
    lateCheckoutType: string;
    lateCheckoutVal: number;
  };

  notFound = false;

  constructor(serverData?: Dict) {
    if (!serverData || !serverData.success || !serverData.data) {
      this.id = 0;
      this.notFound = true;
      return;
    }

    const { data } = serverData;

    this.id = parseInt(data.property_id);
    this.code = data.code;
    this.name = data.hotel_name;
    this.picture = new Image({
      src: data.logo_original,
      thumb: data.logo,
      alt: data.hotel_name,
    });
    this.coverImage = new Image({
      src: data.logo_original,
      thumb: data.logo,
      alt: data.hotel_name,
    });
    this.description = new MultilangText(data.hotel_about_langs);
    this.address = data.hotel_address;
    this.images = (data.additional_photos || []).map(
      (image) =>
        new Image({ src: image.path, thumb: image.thumb_path, alt: image.alt })
    );
    this.cancellationPolicy = new MultilangText(
      data.custom_cancellation_policy_langs
    );
    this.termsAndConditions = new MultilangText(data.terms_txt_langs);
    this.sameDayReservation = data.same_day_reservation === '1';

    this.bankTransferInstructions = new MultilangText(
      data.bank_transfer_instruction
    );

    this.paymentMethods = {
      creditCardsEnabled: data.credit_cards_enabled || false,
      bankTransfersEnabled: data.bank_transfers_enabled || false,
      paypalEnabled: data.paypal_enabled || false,
      supportedCardTypes: data.payments_credit_cards.map(
        ({ payment_code }) => payment_code
      ),
    };

    this.defaultCurrency = data.default_currency?.toLowerCase() || 'usd';
    this.defaultLanguage = data.default_lang?.toLowerCase() || 'en';

    this.custom_fields = data.custom_fields || {};

    this.amenities = Object.values(data.hotel_features).map(
      ({ name, slug, lang }: any) => ({
        name,
        slug,
        lang: new MultilangText(lang),
      })
    );

    this.additionalAmenities = Object.keys(data.hotel_features_custom).map(
      (name: string) => ({
        name,
      })
    );

    this.contact = {
      firstName: data.contact_first_name,
      lastName: data.contact_last_name,
      phone: data.hotel_phone,
      email: data.hotel_email,
    };

    this.policies = {
      checkin: data.terms_normal_checkin,
      checkout: data.terms_normal_checkout,
      lateCheckout: data.terms_late_checkout === '1',
      lateCheckoutHour: data.terms_late_checkout_hour,
      lateCheckoutType: data.terms_late_checkout_type,
      lateCheckoutVal: parseFloat(data.terms_late_checkout_val),
    };

    this.settings = {
      showMap: !(data.settings?.hideMap === '1'),
      askPostalAddress: data.settings?.bookingPostalAddress === '1' || false,
      askNationality: data.settings?.bookingNationality === '1' || false,
      askGender: data.settings?.bookingGender === '1' || false,
      askGuestTaxID: data.settings?.askGuestTaxIdNumber === '1' || false,
      askCompanyName: data.settings?.askCompanyName === '1' || false,
      askCompanyTaxID: data.settings?.askCompanyTaxIdNumber === '1' || false,
      requirePhoneNumber:
        data.settings?.bookingRequireTelephone === '1' || false,
      askEstimatedArrivalTime:
        data.settings?.bookingEstimatedArrivalTime === '1' || false,
      requireAcceptTerms:
        data.settings?.bookingRequireTermsConditions === '1' || false,
      redirectAfterReservation: !!data.settings?.bookingRedirectUrl,
      redirectUrl: data.settings?.bookingRedirectUrl?.url || '',
      redirectDelay: parseInt(data.settings?.bookingRedirectUrl?.time || 0),
      requireCvv: data.settings?.bookingSettingsCvv === '1' || false,
    };

    this.roomsSourceSettings = [];
    if (data.settings?.roomTypeBookingSettings) {
      for (let i = 0; i < data.settings?.roomTypeBookingSettings.length; i++) {
        const setting = data.settings?.roomTypeBookingSettings[i];
        this.roomsSourceSettings.push({
          canSelectSpecificRoom: setting.bookRoomsOnMyBookings === '1',
          bookableLimit: parseInt(setting.bookableLimit),
          capacity: parseInt(setting.capacity),
          maxRooms: parseInt(setting.maxRooms),
          roomTypeId: setting.roomTypeId,
          title: setting.title,
        });
      }
    }
  }
}
